import {
	MouseEvent,
	ReactElement,
	Ref,
	RefObject,
	forwardRef,
	useCallback,
	useContext,
	useImperativeHandle,
	useMemo,
	useRef,
	KeyboardEvent
} from "react";
import {
	Icon,
	IconValue,
	isSvgIcon,
	unsetIconColor
} from "../Icon/Icon";
import { GridItemContext } from "./GridItem";
import classNames from "classnames";
import styles from "./GridItemContent.module.less";
import { Checkbox } from "../Checkbox/Checkbox";
import { ActionAndOthers } from "client_legacy/ndaw/actions";
import {
	actionComponentFactory,
	computeActionsToRender
} from "../ActionList/actionsRenderHelpers";
import { useKeyboardListNavigationItem } from "client/Hooks";
import { Key } from "client/Misc/commonTypes";
import { setTrackingId } from "client/Misc/utils";
import { GridItemMoreActions, GridItemMoreActionsHandle } from "./GridItemMoreActions";

export type GridItemContentProps<TActionContext> = {
	name: string;
	thumbnail?: ReactElement;
	icons?: readonly IconValue[];
	checkbox?: GridItemCheckbox;
	onTitleClick?(): void;
} & ({
	actions?: undefined;
} | {
	actions: readonly ActionAndOthers<TActionContext>[];
	actionContext: TActionContext;
});

export type GridItemContentHandle = {
	closeActions(): void;
};

const GridItemContentInternal = forwardRef(
	<TActionContext, >(props: GridItemContentProps<TActionContext>, ref: Ref<GridItemContentHandle>): ReactElement => {
		const {
			name,
			icons,
			thumbnail,
			onTitleClick
		} = props;

		const moreActionsRef: RefObject<GridItemMoreActionsHandle | null> = useRef<GridItemMoreActionsHandle>(null);

		const {
			isInteracting,
			isDisabled,
			isFocused
		} = useContext(GridItemContext);

		const titleClickHandler = useCallback(
			(args: MouseEvent): void => {
				if (onTitleClick) {
					args.stopPropagation();
					onTitleClick();
				}
			},
			[onTitleClick]
		);

		const actionsToRender = useMemo(
			(): readonly ActionAndOthers<TActionContext>[] | null => {
					if (!props.actions) {
						return null;
					}

					return computeActionsToRender(
						props.actions,
						props.actionContext
					);
				},
			[props]
		);

		useImperativeHandle(
			ref,
			(): GridItemContentHandle => ({
				closeActions: (): void => {
					moreActionsRef.current
						?.closeActions?.();
				},
			}),
			[]
		);

		return (
			<>
				<div
					className={classNames(
						styles.root,
						{[styles.root_disabled]: isDisabled}
					)}
				>
					<div className={styles.header}>
						<div className={styles.thumbnail}>
							{thumbnail}
						</div>
						<div
							className={classNames(
								styles.tools,
								{
									[styles.tools_isVisible]: isInteracting || isFocused,
								}
							)}
						>
							{props.actions !== undefined
								&& actionsToRender
								&& actionsToRender.length > 0
								&& (
									<GridItemMoreActions
										menuTitle={name}
										actions={actionsToRender}
										actionContext={props.actionContext}
										tabIndex={isFocused ? 0 : -1}
										ref={moreActionsRef}
									/>
								)
							}
							{props.checkbox && (
								<Checkbox
									value={props.checkbox.isChecked}
									onChange={props.checkbox.onIsCheckChange}
									shouldBubbleClick={false}
									tabIndex={isFocused ? 0 : -1}
								/>
							)}
						</div>
					</div>
					<h4
						className={classNames(
							styles.name,
							{[styles.name_isClickable]: onTitleClick}
						)}
						onClick={titleClickHandler}
					>
						{icons && icons.map(
							(x, i) => (
								<span
									key={i}
									className={classNames(
										styles.icon,
										{ [styles.icon_svg]: isSvgIcon(x)}
									)}
								>
									<Icon
										value={isDisabled
											? unsetIconColor(x)
											: x
										}
									/>
								</span>
							)
						)}
						<span
							{...setTrackingId("name")}
							title={name}
						>
							{name}
						</span>
					</h4>
				</div>
			</>
		);
	}
);

GridItemContentInternal.displayName = "GridItemContent";

type GridItemContentWithDisplayName = {
	<TActionContext, >(props: GridItemContentProps<TActionContext> & { ref?: Ref<GridItemContentHandle>; }): ReactElement;
	displayName: string;
};

export const GridItemContent = GridItemContentInternal as GridItemContentWithDisplayName;

export type GridItemCheckbox = {
	isChecked: boolean;
	onIsCheckChange(isChecked: boolean): void;
};

type NavigableActionItemProps<TActionContext> = {
	action: ActionAndOthers<TActionContext>;
	actionContext: TActionContext;
	index: number;
	onCloseMenu: () => void;
};

const NavigableActionItem = <TActionContext, >(
	props: NavigableActionItemProps<TActionContext>
): ReactElement => {
	const {
		action,
		actionContext,
		index,
		onCloseMenu
	} = props;

	const {
		onKeyDown,
		ref
	} = useKeyboardListNavigationItem({
		itemIndex: index,
		onExecute: (): void => {
			if (action.canExecute?.(actionContext)) {
				action.execute?.(actionContext);
				onCloseMenu();
			}
		},
		executeKey: [
			Key.Enter,
			Key.Space
		],
		shouldCancelOnTab: true
	});

	const KeyDownHandler = useCallback(
		(args: KeyboardEvent<HTMLLIElement>): void => {
			onKeyDown(args);
			args.stopPropagation();
		},
		[onKeyDown]
	);

	return actionComponentFactory(
		action,
		actionContext,
		{
			ref: ref,
			onKeyDown: KeyDownHandler,
			tabIndex: -1
		}
	);
};
NavigableActionItem.displayName = "NavigableActionItem";